import { useMemo } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Grid } from "@react-three/drei";
import type { ClosetSpec } from "../domain/types";
import { ceilingHeightAt, panelXPositions } from "../domain/parts";

interface Props {
  spec: ClosetSpec;
}

/**
 * 3D preview. Centimetres map 1:1 to scene units, then we scale the whole rig
 * down so a 240 cm-wide closet fits in the viewport.
 */
export function ClosetPreview({ spec }: Props): JSX.Element {
  const { totalWidth, depth, material, ceiling, sections } = spec;
  const t = material.carcassThickness;

  const xs = useMemo(() => panelXPositions(spec), [spec]);

  // Build a sequence of 3D meshes describing the carcass.
  const panels = useMemo(() => {
    const out: JSX.Element[] = [];
    // floor
    out.push(
      <mesh key="floor" position={[totalWidth / 2, t / 2, depth / 2]}>
        <boxGeometry args={[totalWidth, t, depth]} />
        <meshStandardMaterial color="#d4a373" />
      </mesh>,
    );
    // vertical panels — one per xs entry
    xs.forEach((x, i) => {
      const xc = x + (i === xs.length - 1 ? -t / 2 : t / 2);
      const heightL = ceilingHeightAt(spec, x);
      const heightR = ceilingHeightAt(spec, x + t);
      const h = (heightL + heightR) / 2;
      out.push(
        <mesh key={`panel-${i}`} position={[xc, h / 2 + t, depth / 2]}>
          <boxGeometry args={[t, h - t, depth]} />
          <meshStandardMaterial
            color={i === 0 || i === xs.length - 1 ? "#b08968" : "#c9a27a"}
          />
        </mesh>,
      );
    });
    // shelves
    sections.forEach((sec, i) => {
      const xLeft = xs[i] + t;
      const yMin = t + 2;
      const yMaxL = ceilingHeightAt(spec, xs[i]) - 2;
      const yMaxR = ceilingHeightAt(spec, xs[i + 1]) - 2;
      const yMax = Math.min(yMaxL, yMaxR);
      const span = sec.width;
      const shelfYs =
        sec.shelfCount > 0
          ? Array.from(
              { length: sec.shelfCount },
              (_, k) => yMin + ((yMax - yMin) * (k + 1)) / (sec.shelfCount + 1),
            )
          : [];
      shelfYs.forEach((y, k) => {
        out.push(
          <mesh
            key={`shelf-${i}-${k}`}
            position={[xLeft + span / 2, y, depth / 2]}
          >
            <boxGeometry args={[span - 0.0625, t, depth - 1]} />
            <meshStandardMaterial color="#e0c097" />
          </mesh>,
        );
      });
      if (sec.hangingRod) {
        const rodY = (yMin + yMax) / 2;
        out.push(
          <mesh
            key={`rod-${i}`}
            position={[xLeft + span / 2, rodY, depth / 2]}
            rotation={[0, 0, Math.PI / 2]}
          >
            <cylinderGeometry args={[0.5, 0.5, span - 1, 16]} />
            <meshStandardMaterial
              color="#888"
              metalness={0.6}
              roughness={0.4}
            />
          </mesh>,
        );
      }
    });
    // ceiling reference (translucent)
    if (ceiling.type !== "flat") {
      const yL = ceilingHeightAt(spec, 0);
      const yR = ceilingHeightAt(spec, totalWidth);
      const yC = ceilingHeightAt(spec, totalWidth / 2);
      const verts =
        ceiling.type === "gable"
          ? new Float32Array([
              0,
              yL,
              0,
              totalWidth / 2,
              yC,
              0,
              totalWidth,
              yR,
              0,
              0,
              yL,
              depth,
              totalWidth / 2,
              yC,
              depth,
              totalWidth,
              yR,
              depth,
            ])
          : new Float32Array([
              0,
              yL,
              0,
              totalWidth,
              yR,
              0,
              0,
              yL,
              depth,
              totalWidth,
              yR,
              depth,
            ]);
      const indices =
        ceiling.type === "gable"
          ? new Uint16Array([0, 3, 1, 1, 3, 4, 1, 4, 2, 2, 4, 5])
          : new Uint16Array([0, 1, 2, 1, 3, 2]);
      out.push(
        <mesh key="ceiling">
          <bufferGeometry>
            <bufferAttribute attach="attributes-position" args={[verts, 3]} />
            <bufferAttribute attach="index" args={[indices, 1]} />
          </bufferGeometry>
          <meshStandardMaterial
            color="#a0c4ff"
            transparent
            opacity={0.25}
            side={2}
          />
        </mesh>,
      );
    }
    return out;
  }, [spec, xs, ceiling.type, depth, sections, t, totalWidth]);

  // Center camera on the closet
  const center: [number, number, number] = [
    totalWidth / 2,
    ceiling.heightLeft / 2,
    depth / 2,
  ];

  return (
    <Canvas
      camera={{
        position: [totalWidth * 1.4, ceiling.heightLeft * 1.1, depth * 4],
        fov: 35,
      }}
    >
      <ambientLight intensity={0.6} />
      <directionalLight position={[100, 200, 100]} intensity={0.8} />
      <directionalLight position={[-100, 200, -50]} intensity={0.3} />
      <Grid
        args={[200, 200]}
        cellColor="#999"
        sectionColor="#666"
        infiniteGrid
      />
      <group>{panels}</group>
      <OrbitControls target={center} makeDefault />
    </Canvas>
  );
}
